import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { ActiveCompanyProvider } from '@/contexts/ActiveCompanyContext';
import { ThemeModeProvider, useThemeMode } from '@/contexts/ThemeModeContext';
import { useLocale } from '@/i18n/useLocale';
import { ltrCache, rtlCache } from '@/theme/emotionCache';
import { createAppTheme } from '@/theme';
import GlobalToast from '@/components/common/GlobalToast';
import { errorMessageFrom, showToast, successMessageFrom } from '@/lib/toast';
import AppRoutes from '@/routes/AppRoutes';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
  mutationCache: new MutationCache({
    onSuccess: (data, _variables, _context, mutation) => {
      if (mutation.meta?.silent) return;
      showToast(successMessageFrom(data), 'success');
    },
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.silent) return;
      showToast(errorMessageFrom(error), 'error');
    },
  }),
});

function ThemedApp() {
  const { mode } = useThemeMode();
  const { direction } = useLocale();
  const cache = direction === 'rtl' ? rtlCache : ltrCache;
  const theme = createAppTheme(mode, direction);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalToast />
        <BrowserRouter>
          <AuthProvider>
            <ActiveCompanyProvider>
              <AppRoutes />
            </ActiveCompanyProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <ThemedApp />
      </ThemeModeProvider>
    </QueryClientProvider>
  );
}
